import { getNotificationsData } from '@api/notifications';
import noNotificationArt from '@assets/misc/no-notification.svg';
import BackgroundLetterAvatars from '@components/ui/background-letter-avatars';
import HeadingArrowWrapper from '@components/ui/heading-arrow-wrapper';
import { displayTime, getTimeAgo } from '@global/helpers/general';
import useInfiniteScroll from '@global/hooks/use-infinite-scroll';
import {
  Avatar,
  Button,
  CircularProgress,
  Divider,
  Grid,
  ListItemAvatar,
  Tooltip,
  Typography
} from '@mui/material';
import { navigateTo } from '@services/navigation';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const LIMIT = 10;

export default function Notifications({ hideHeading = false }) {
  const location = useLocation();
  const notificationsContainerRef = useRef(null);

  const {
    scroll,
    fetchFn,
    list: ntfs,
    loading
  } = useInfiniteScroll({
    LIMIT,
    fetchData: getNotificationsData,
    ref: notificationsContainerRef
  });

  useEffect(() => {
    fetchFn(0, 20);
  }, [location?.pathname]);

  return (
    <HeadingArrowWrapper
      sx={{
        padding: '0px 1em',
        width: '96%',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        margin: '0em 0 0em 1em',
        height: '100%'
      }}
      title={!hideHeading ? 'Notifications' : ''}
      disableBack={hideHeading}>
      {!['notifications'].includes(location.pathname.split('/')[1]) ? (
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ padding: '1em 0', width: '100%', flexWrap: 'nowrap' }}>
          <Typography
            variant="body1"
            sx={{ textAlign: 'left', fontWeight: 600, fontSize: '1.6rem' }}>
            Notifications
          </Typography>
          <Button
            variant="text"
            disabled={ntfs?.length === 0}
            sx={{
              width: 'fit-content',
              fontWeight: 600,
              fontSize: '1.6rem',
              padding: 0
            }}
            onClick={() => {
              navigateTo('/notifications');
            }}>
            See All
          </Button>
        </Grid>
      ) : (
        <Grid
          sx={{
            //  maxWidth: '400px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            //  height: '100%',
            padding: '0 1em',
            margin: 'auto'
          }}
          title={'Notifications'}></Grid>
      )}
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ padding: '1em 0', width: '100%', height: '100%', minHeight: 0 }}>
        {ntfs?.length > 0 ? (
          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="stretch"
            spacing={{ xs: 2, md: 3 }}
            columns={{ xs: 1, sm: 12, md: 12, lg: 12 }}
            sx={{
              backgroundColor: '#ffffff',
              rowGap: '1em',
              columnGap: '1em',
              padding: '0',
              margin: '1em 0 !important',
              overflowY: 'auto',
              width: '100% !important',
              paddingBottom: '1em',
              overflowX: 'hidden',
              scrollBehavior: 'smooth',
              height: '100%'
            }}
            onScroll={scroll}
            ref={notificationsContainerRef}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                overflowX: 'hidden',
                width: '100%',
                margin: 0,
                height: 'fit-content'
              }}>
              {ntfs?.map((x: any, i: number) => (
                <>
                  <Grid
                    container
                    sx={{
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: { xs: 'center', sm: 'flex-start' },
                      alignItems: { xs: 'center', sm: 'flex-start' },
                      margin: '1em 0'
                    }}>
                    <Grid
                      item
                      sx={{ width: 'fit-content', marginRight: '0px', textAlign: 'center' }}>
                      <ListItemAvatar
                        sx={{
                          padding: '0em',
                          display: 'flex',
                          justifyContent: { xs: 'center', sm: 'flex-start' }
                        }}>
                        {x?.image ? (
                          <Avatar src={x?.image ?? ''} />
                        ) : (
                          <BackgroundLetterAvatars
                            name={x?.title ?? ''}
                            sx={{ width: '35px', height: '35px' }}
                          />
                        )}
                      </ListItemAvatar>
                    </Grid>
                    <Grid item sx={{ flex: 'auto', width: { xs: '100%', sm: '55%' } }}>
                      <Typography
                        variant="body1"
                        sx={{
                          padding: 0,
                          margin: 0,
                          marginTop: { xs: '0px', sm: '-2.5px' },
                          fontWeight: 600,
                          textAlign: { xs: 'center', sm: 'left' },
                          overflowWrap: 'break-word'
                        }}>
                        {x?.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          textAlign: { xs: 'center', sm: 'left' },
                          overflowWrap: 'anywhere',
                          color: 'font.medium'
                        }}>
                        {x?.description}
                      </Typography>

                      <Tooltip title={displayTime(x?.createdAt, 'DD MMM, YYYY hh:mm:A')}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            width: 'fit-content',
                            marginLeft: 'auto',
                            color: 'font.main'
                          }}>
                          {getTimeAgo(x?.createdAt, true)}
                        </Typography>
                      </Tooltip>
                    </Grid>
                  </Grid>
                  {i != ntfs?.length - 1 ? <Divider sx={{ width: '100%' }} /> : null}
                </>
              ))}
            </div>
          </Grid>
        ) : null}
        {loading ? (
          <Grid container direction="row" justifyContent="center" alignItems="center">
            <CircularProgress />
          </Grid>
        ) : ntfs?.length <= 0 && !loading ? (
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
            sx={{ height: '100%', transform: 'translate(0, -5%)' }}>
            <img src={noNotificationArt} alt="No notifications found illustration" />
            <div
              style={{
                display: 'flex',
                flexFlow: 'column',
                rowGap: '1rem',
                alignItems: 'center',
                margin: '0 14%',
                marginTop: '2rem'
              }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: '600', fontFamily: 'Poppins', textAlign: 'center' }}>
                No Notifications yet
              </Typography>
              <Typography
                variant="body2"
                sx={{ textAlign: 'center', color: 'font.medium', lineHeight: '16px' }}>
                Once you start receiving notifications, they will show up here.
              </Typography>
            </div>
          </Grid>
        ) : null}
      </Grid>
    </HeadingArrowWrapper>
  );
}
